import React, { PureComponent } from 'react';
import { themr } from 'react-css-themr';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import compose from 'recompose/compose';

import ContactActions from 'common/redux/actions/contact';
import ToastActions from 'common/redux/actions/toast';
import { dashboardColors } from 'common/enums/ColorEnum';
import AppPreferencesActions from 'common/redux/actions/appPreferences';

import {
    clientSelector,
    isAddEntityActive as isAddEntityActiveSelector,
    isImportEntityActive as isImportEntityActiveSelector,
    isExportEntityActive as isExportEntityActiveSelector,
    editEntityIdSelector
} from 'common/redux/selectors/queryParamsSelectors';
import { getEntitySettings } from 'common/redux/selectors/clientSelector';
import { getEntityTablePreferences } from 'common/redux/selectors/appPreferences';
import ContactMasterSelectors from 'common/redux/selectors/contactMaster';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';

import { Checkbox } from 'common/components/core/checkbox';
import MasterExportContainer from 'common/entity/generic/MasterExportContainer';
import { withRouter } from 'common/components/core/router';
import theme from 'common/entity/BulkEditContainer.scss';

import { entityMasterFactory } from 'common/entity/generic/MasterContainer';
import MasterContainerHOC from 'common/entity/generic/MasterContainerHOC';
import {
    CellDataTypes,
    FROZEN_COLUMN_LIMIT
} from 'common/components/core/table';
import {
    ConnectedTeamSelectorFilter,
    ConnectedTeamIdSelectorFilter
} from 'common/entity/components/TeamSelectorFilter';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import { ContactType } from 'common/models/internal/Contact';
import TeamList from 'common/components/core/common/TeamList';
import VisitUtils from 'common/utils/VisitUtils';
import SingleContactEditContainer from 'common/entity/contactMaster/SingleContactEditContainer';
import FuelTypeFilter from 'common/entity/components/FuelTypeFilter';
import ContactMasterImportContainer from 'common/entity/contactMaster/ContactMasterImportContainer';
import {
    ENTITY_NAME_ID,
    ENTITY_EXPORT_ID,
    ENTITY_TABLE_ID
} from 'common/constants/entity/contactMaster';
import { RoleOperationEnum } from 'common/modules/rbac';

const selectTeamId = ({ teamId }) => teamId;

const getTeamIdFilter = ({ clientId, filteredColumn }) => {
    const {
        value: { allowEmptyOrNull, values }
    } = filteredColumn;
    const teamValueFilter = values.map(team => ({
        clientId,
        teamId: team
    }));

    return new FilterBuilder()
        .withName('teams')
        .withOperation(FilterOperation.Contains)
        .withValues(teamValueFilter)
        .withAllowEmptyOrNull(allowEmptyOrNull)
        .withCaseSensitive(true);
};

const DEFAULT_COLUMNS = [
    {
        id: 'selector',
        Cell: props => {
            const {
                tdProps: {
                    rest: { isChecked, onCheckboxClick }
                }
            } = props;
            return (
                <Checkbox
                    checkboxColor={dashboardColors.darkSkyBlue.name}
                    className={theme.selectionCell}
                    theme={theme}
                    checked={isChecked}
                    onChange={onCheckboxClick}
                />
            );
        },
        sortable: false,
        filterable: false,
        frozen: true,
        disablePinControl: true,
        width: 40
    },
    {
        headerTextId: 'contactTable.id',
        accessor: 'id',
        minWidth: 100,
        infixSearchable: true,
        frozen: true
    },
    {
        headerTextId: 'contactTable.code',
        accessor: 'code',
        frozen: true,
        infixSearchable: true,
        show: false
    },
    {
        headerTextId: 'contactTable.name',
        accessor: 'name'
    },
    {
        headerTextId: 'contactTable.type',
        accessor: 'type',
        FilterComponent: FuelTypeFilter,
        Cell: props => {
            const {
                value,
                tdProps: {
                    rest: { t, theme }
                }
            } = props;
            const cellText = t(`contactProperties.contactType.${value}`);
            return (
                <div title={cellText} className={theme.cellText}>
                    {cellText}
                </div>
            );
        },
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('type')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = ContactType.getOptions({
                translationKey: 'contactProperties.contactType'
            });
            return options;
        }
    },
    {
        headerTextId: 'contactTable.primaryMobileNumber',
        accessor: 'phoneNumber.phoneNumber'
    },
    {
        headerTextId: 'contactTable.alternateMobileNumber',
        accessor: 'additionalPhoneNumbers',
        sortable: false,
        filterable: false,
        Cell: props => {
            const {
                original: { additionalPhoneNumbers }
            } = props;
            const cellText = additionalPhoneNumbers
                .map(additionalPhoneNumber => additionalPhoneNumber.phoneNumber)
                .join(', ');
            return <span>{cellText}</span>;
        }
    },
    {
        headerTextId: 'contactTable.landlineNumber',
        accessor: 'landlineNumber.phoneNumber'
    },
    {
        headerTextId: 'contactTable.primaryEmailAddress',
        accessor: 'emailAddress.emailAddress'
    },
    {
        headerTextId: 'contactTable.alternateEmailAddress',
        accessor: 'additionalEmailAddresses',
        sortable: false,
        filterable: false,
        Cell: props => {
            const {
                original: { additionalEmailAddresses }
            } = props;
            return (
                <span>
                    {additionalEmailAddresses.getIn([0, 'emailAddress'])}
                </span>
            );
        }
    },
    {
        headerTextId: 'contactTable.company',
        accessor: 'company'
    },
    {
        headerTextId: 'contactTable.teamName',
        id: 'teams.name',
        accessor: 'teams',
        exportId: 'teams.name',
        sortable: false,
        minWidth: 200,
        FilterComponent: ConnectedTeamSelectorFilter,
        getCustomFilterInfo: getTeamIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, teams }
                }
            } = props;
            const teamIds = original.teams;
            return <TeamList teams={teams} teamIds={teamIds} theme={theme} />;
        }
    },
    {
        headerTextId: 'contactTable.teamId',
        id: 'teams.id',
        accessor: 'teams',
        exportId: 'teams.id',
        sortable: false,
        minWidth: 200,
        FilterComponent: ConnectedTeamIdSelectorFilter,
        getCustomFilterInfo: getTeamIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, teams }
                }
            } = props;
            const teamIds = original.teams;
            return (
                <TeamList
                    teams={teams}
                    teamIds={teamIds}
                    theme={theme}
                    selectTeamName={selectTeamId}
                />
            );
        }
    },
    {
        headerTextId: 'contactTable.updatedBy',
        id: 'updatedBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.updatedBy',
        Cell: props => {
            const {
                original: original,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const updatedBy = VisitUtils.formatIDs(
                original?.auditMetadata?.updatedBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={updatedBy} className={theme.cellText}>
                    {updatedBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'contactTable.updatedOn',
        accessor: 'auditMetadata.updatedOn',
        id: 'updatedOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'contactTable.createdBy',
        id: 'createdBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.createdBy',
        Cell: props => {
            const {
                original: original,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const createdBy = VisitUtils.formatIDs(
                original?.auditMetadata?.createdBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={createdBy} className={theme.cellText}>
                    {createdBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'contactTable.createdOn',
        accessor: 'auditMetadata.createdOn',
        id: 'createdOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    }
];

const contactMasterContainerFactory = ({
    EntityEditViewContainer = SingleContactEditContainer,
    EntityImportContainer = ContactMasterImportContainer,
    COLUMNS = DEFAULT_COLUMNS,
    options = {
        entityTableId: ENTITY_TABLE_ID,
        operations: {
            edit: RoleOperationEnum.UPDATE_CONTACT,
            add: RoleOperationEnum.UPDATE_CONTACT,
            export: RoleOperationEnum.EXPORT_CONTACT
        }
    },
    ActionsBar,
    BulkViewEmptyState,
    fetchEntities = ContactActions.fetchActiveEntities
}) => {
    const ContactMaster = entityMasterFactory({
        EntityEditViewContainer,
        EntityImportContainer,
        EntityExportContainer: MasterExportContainer,
        COLUMNS,
        options,
        ActionsBar,
        BulkViewEmptyState
    });
    class ContactMasterContainer extends PureComponent {
        getTdProps = (state, rowInfo) => {
            if (!rowInfo) {
                return {};
            }
            const {
                original: { id }
            } = rowInfo;
            const {
                t,
                entitySettings,
                selectedRows,
                toggleRowSelection,
                theme,
                teams,
                isGlobalPersonnel
            } = this.props;
            const isChecked = selectedRows.indexOf(id) >= 0;
            return {
                onCheckboxClick: () => {
                    toggleRowSelection(id);
                },
                t,
                entitySettings,
                theme,
                isChecked,
                teams,
                isGlobalPersonnel,
                className: classnames({ [theme.selectedRow]: isChecked })
            };
        };

        render() {
            const { clientId, fetchOptions, t } = this.props;
            const contactMasterFetchOptions = {
                clientId,
                ...(fetchOptions || {})
            };
            return (
                <ContactMaster
                    {...this.props}
                    emptyStateImageSrc="/img/masterContact-empty.svg"
                    entityNameId={ENTITY_NAME_ID}
                    entityExportId={ENTITY_EXPORT_ID}
                    entityId="contactId"
                    entitySettingsName="contactSettings"
                    fetchOptions={contactMasterFetchOptions}
                    getTdProps={this.getTdProps}
                    hasFreezableColumns
                    frozenColumnLimit={FROZEN_COLUMN_LIMIT}
                    pinTooltip={t('pinTooltip')}
                    unpinTooltip={t('unpinTooltip')}
                />
            );
        }
    }

    const mapStateToProps = (state, ownProps) => {
        const clientId = clientSelector(state, ownProps);
        const isLoading = ContactMasterSelectors.isEntitiesLoading(
            state,
            ownProps
        );
        const data = ContactMasterSelectors.getEntities(state, ownProps);
        const isAddEntityActive = isAddEntityActiveSelector(state, ownProps);
        const entityIdToEdit = editEntityIdSelector(state, ownProps);
        const entitySettings = getEntitySettings(state, ownProps);
        const tableSettings = getEntityTablePreferences(state, ENTITY_TABLE_ID);
        const isImportEntityActive = isImportEntityActiveSelector(
            state,
            ownProps
        );
        const isExportEntityActive = isExportEntityActiveSelector(
            state,
            ownProps
        );
        const teams = teamsSelector(state, ownProps);
        const isGlobalPersonnel = isGlobalPersonnelSelector(state, ownProps);

        return {
            clientId,
            isLoading,
            data,
            isAddEntityActive,
            entityIdToEdit,
            entitySettings,
            tableSettings,
            isImportEntityActive,
            isExportEntityActive,
            teams,
            isGlobalPersonnel
        };
    };

    const withRedux = connect(mapStateToProps, {
        fetchEntities,
        showToastAction: ToastActions.showToast,
        updateToastAction: ToastActions.updateToast,
        hideToastAction: ToastActions.hideToast,
        disableEntity: ContactActions.disableEntity,
        clearData: ContactActions.clearData,
        saveTableSettings: AppPreferencesActions.saveEntityTablePreferences,
        bulkDisableEntity: ContactActions.bulkDisableEntity
    });

    const enhance = compose(
        themr(null, theme),
        withRouter,
        withRedux,
        translate('entityManagement'),
        MasterContainerHOC
    );
    return enhance(ContactMasterContainer);
};

const ThemedConnectedContactMasterContainer = contactMasterContainerFactory({});

export {
    contactMasterContainerFactory,
    ThemedConnectedContactMasterContainer as ContactMasterContainer
};
export default ThemedConnectedContactMasterContainer;
